import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Warning, Wrench, ArrowLeft, Copy, DownloadSimple, ArrowsClockwise, CheckCircle,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";

// Sunday morning, one-handed, in a parking lot. Note DK's NATIVE APP cannot
// take a CSV upload — bulk upload only exists on the DK website — so the
// primary output here is copyable swap instructions you can tap into the app,
// with the CSV kept as the desktop/mobile-browser path.
const BOOKS = [
  { key: "smart", label: "Smart" },
  { key: "raw", label: "Manual" },
];

const lineupMap = (lineups) => {
  const m = {};
  for (const lu of lineups || []) {
    m[String(lu.lineup_id)] = (lu.players || []).map((p) => ({ id: String(p.dk_id), name: p.name }));
  }
  return m;
};

export default function MobileSwap() {
  const [book, setBook] = useState("smart");
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [instructions, setInstructions] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setStatus(await api.sundayStatus(book));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load your lineups");
    } finally {
      setLoading(false);
    }
  }, [book]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [load]);

  const runFix = async (dkId, name) => {
    setBusy(dkId || "all");
    try {
      const before = lineupMap((await api.getLineups()).lineups);
      const r = dkId ? await api.hubRebuildAffected(dkId, book) : await api.fixInactive(book);
      const after = lineupMap((await api.getLineups()).lineups);
      const steps = [];
      for (const [lid, rows] of Object.entries(after)) {
        const was = before[lid];
        if (!was) continue;
        const outs = was.filter((p) => !rows.some((q) => q.id === p.id));
        const ins = rows.filter((p) => !was.some((q) => q.id === p.id));
        if (outs.length) {
          steps.push({
            lineup: lid,
            text: `Lineup ${lid}: ${outs.map((o) => o.name).join(", ")} → ${ins.map((i) => i.name).join(", ") || "(empty)"}`,
          });
        }
      }
      setInstructions(steps);
      const n = (r.fixed || []).length;
      toast.success(n ? `Fixed ${n} lineup${n === 1 ? "" : "s"}` : `Nothing to fix${name ? ` for ${name}` : ""}`, {
        description: (r.failed || []).length ? `${r.failed.length} couldn't be swapped.` : undefined,
      });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Fix failed");
    } finally {
      setBusy("");
    }
  };

  const copyAll = async () => {
    const txt = instructions.map((s) => s.text).join("\n");
    try {
      await navigator.clipboard.writeText(txt);
      toast.success("Swap list copied", { description: "Paste it anywhere, then make the edits in the DK app." });
    } catch {
      toast.error("Clipboard blocked — long-press the list to copy");
    }
  };

  const inactive = status?.inactive_players || [];

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-zinc-100 pb-24" data-testid="mobile-swap">
      <header className="sticky top-0 z-20 bg-[#0a0a0a]/95 backdrop-blur border-b border-zinc-800 px-4 py-3">
        <div className="flex items-center gap-3">
          <Link to="/app" data-testid="mswap-back" className="text-zinc-500 hover:text-white"><ArrowLeft size={18} /></Link>
          <div className="min-w-0">
            <div className="heading font-black text-lg leading-tight">Sunday Swap</div>
            <div className="mono text-[10px] text-zinc-500">
              {loading ? "checking your lineups…" : `${status?.lineups || 0} lineups · ${inactive.length} ruled out`}
            </div>
          </div>
          <button type="button" onClick={load} data-testid="mswap-refresh"
            className="ml-auto p-2 border border-zinc-800 text-zinc-400 active:bg-zinc-900">
            <ArrowsClockwise size={16} />
          </button>
        </div>
        <div className="flex gap-2 mt-2">
          {BOOKS.map((b) => (
            <button key={b.key} type="button" onClick={() => setBook(b.key)}
              data-testid={`mswap-book-${b.key}`}
              className={`flex-1 py-2 mono text-[11px] uppercase tracking-widest border ${
                book === b.key ? "border-[#39FF14] text-[#39FF14] bg-[#39FF14]/10 font-bold" : "border-zinc-800 text-zinc-500"
              }`}
            >{b.label}</button>
          ))}
        </div>
      </header>

      <main className="px-4 py-4 space-y-4">
        {!loading && inactive.length === 0 && (
          <div className="border border-[#39FF14]/40 bg-[#39FF14]/5 p-4 text-center" data-testid="mswap-clean">
            <CheckCircle size={28} weight="fill" className="text-[#39FF14] mx-auto" />
            <div className="heading font-black text-base mt-2">Portfolio is clean</div>
            <div className="mono text-[11px] text-zinc-400 mt-1">
              No ruled-out players in your {book === "raw" ? "Manual" : "Smart"} lineups.
            </div>
          </div>
        )}

        {inactive.length > 0 && (
          <>
            <button
              type="button"
              onClick={() => runFix(null)}
              disabled={!!busy}
              data-testid="mswap-fix-all"
              className="w-full py-4 bg-[#FF3B30] active:bg-[#d92e24] disabled:opacity-60 text-white font-black uppercase tracking-widest text-sm inline-flex items-center justify-center gap-2"
            >
              <Wrench size={16} weight="fill" />
              {busy === "all" ? "Fixing…" : `Fix all ${inactive.length}`}
            </button>

            <div className="space-y-2" data-testid="mswap-list">
              {inactive.map((p) => (
                <div key={p.dk_id} className="border border-zinc-800 bg-[#0e0e0e] p-3 flex items-center gap-3"
                     data-testid={`mswap-player-${p.dk_id}`}>
                  <Warning size={18} weight="fill" className="text-[#FF3B30] flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="font-bold text-[15px] leading-tight truncate">{p.name}</div>
                    <div className="mono text-[10px] text-zinc-500">
                      {p.position} · {p.team} · {p.status || "OUT"}
                    </div>
                  </div>
                  <button type="button" onClick={() => runFix(p.dk_id, p.name)} disabled={!!busy}
                    data-testid={`mswap-fix-${p.dk_id}`}
                    className="px-3 py-2.5 border border-[#FFCC00] text-[#FFCC00] mono text-[11px] uppercase tracking-widest font-bold active:bg-[#FFCC00] active:text-black disabled:opacity-40">
                    {busy === p.dk_id ? "…" : "Fix"}
                  </button>
                </div>
              ))}
            </div>
          </>
        )}

        {instructions.length > 0 && (
          <section className="border border-[#39FF14]/40 bg-black p-3" data-testid="mswap-instructions">
            <div className="flex items-center justify-between gap-2">
              <div className="overline text-[#39FF14]">Make these edits in the DK app</div>
              <button type="button" onClick={copyAll} data-testid="mswap-copy"
                className="mono text-[10px] uppercase tracking-widest px-2 py-1 border border-zinc-700 text-zinc-300 inline-flex items-center gap-1 active:bg-zinc-800">
                <Copy size={11} /> Copy
              </button>
            </div>
            <div className="mono text-[11px] text-zinc-300 mt-2 space-y-1 select-all">
              {instructions.map((s) => <div key={s.lineup}>{s.text}</div>)}
            </div>
          </section>
        )}

        <section className="border border-zinc-800 p-3 space-y-2" data-testid="mswap-csv">
          <div className="overline">Re-upload path</div>
          <p className="mono text-[10px] text-zinc-500 leading-relaxed">
            DK's native app can't take a CSV — bulk upload only exists on the DK website. Easiest on a phone:
            make the edits above by hand in the app. If you'd rather upload, open draftkings.com in your browser,
            request the desktop site, go to Lineups → Edit Entries, then upload this file.
          </p>
          <a href={api.exportURL()} data-testid="mswap-download"
            className="w-full py-3 border border-[#39FF14] text-[#39FF14] font-bold uppercase tracking-widest text-[12px] inline-flex items-center justify-center gap-2 active:bg-[#39FF14] active:text-black">
            <DownloadSimple size={15} /> Download DK CSV
          </a>
        </section>

        {(status?.movers || []).length > 0 && (
          <section className="border border-zinc-800 p-3" data-testid="mswap-movers">
            <div className="overline mb-1">Projection movers</div>
            {status.movers.slice(0, 6).map((m) => (
              <div key={m.dk_id} className="flex items-baseline justify-between mono text-[11px] py-0.5">
                <span className="text-zinc-300 truncate">{m.name}</span>
                <span className="text-zinc-600">{m.lineups} lu</span>
                <span className={m.delta_pct > 0 ? "text-[#39FF14]" : "text-[#FF3B30]"}>
                  {m.built} → {m.now} ({m.delta_pct > 0 ? "+" : ""}{m.delta_pct}%)
                </span>
              </div>
            ))}
          </section>
        )}
      </main>
    </div>
  );
}
